import React from "react";

// Paginated-items hook with client-side caching and next-page prefetch.
//
// The "loader" is an async function with the signature:
//   loader(pageIdx, perPage) -> { items: T[], totalCount: number }
//
// Usage (with a real API endpoint):
//
//   const loader = React.useCallback(async (page, per) => {
//     const { parsedResponse } = await apiGet(`/api/v1/homepage/webinars?page=${page + 1}&per_page=${per}`);
//     return { items: parsedResponse.data, totalCount: parsedResponse.meta.total };
//   }, []);
//
//   const { items, pageIdx, setPageIdx, totalPages, loading } =
//     usePaginatedItems({ loader, perPage });
//
// Usage (with a pre-loaded array — e.g. a non-paginated endpoint that
// returns everything in one shot, like /api/v1/homepage/community_threads
// or /api/v1/homepage/certifications):
//
//   const loader = React.useMemo(() => makeSampleLoader(rowsFromApi), [rowsFromApi]);
//   const { items, pageIdx, setPageIdx, totalPages, loading } =
//     usePaginatedItems({ loader, perPage });
//
// Implementation notes:
// - Pages are cached in a ref-backed Map keyed by (perPage, pageIdx) so we
//   don't refetch when navigating back. Changing `perPage` (narrow ↔ wide)
//   invalidates the cache on that dimension only.
// - The next page is prefetched on idle after the current page loads.
// - `loading` reflects *current* page; prefetches never set it.
// - If the loader rejects, the error is swallowed (logged) so one failed
//   section never breaks the whole page.
export function makeSampleLoader(fullArray) {
  return async (pageIdx, perPage) => {
    const start = pageIdx * perPage;
    return {
      items: fullArray.slice(start, start + perPage),
      totalCount: fullArray.length,
    };
  };
}

export default function usePaginatedItems({
  loader,
  perPage,
  initialPage = 0,
}) {
  const [pageIdx, setPageIdx] = React.useState(initialPage);
  const [items, setItems] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  // Cache across renders: Map<"perPage:pageIdx", { items, totalCount }>
  const cacheRef = React.useRef(new Map());
  const cacheKey = React.useCallback((p, pp) => `${pp}:${p}`, []);

  // Invalidate cache when perPage (narrow ↔ wide) or loader changes.
  React.useEffect(() => {
    cacheRef.current.clear();
    setPageIdx(0);
  }, [perPage, loader]);

  // Fetch a page on demand. Returns the cached page if available.
  const fetchPage = React.useCallback(
    async (idx) => {
      const key = cacheKey(idx, perPage);
      const cached = cacheRef.current.get(key);
      if (cached) return cached;

      try {
        const result = await loader(idx, perPage);
        const next = {
          items: result.items || [],
          totalCount: result.totalCount || 0,
        };
        cacheRef.current.set(key, next);
        return next;
      } catch (err) {
        if (typeof console !== "undefined") {
          console.warn("[usePaginatedItems] loader failed:", err);
        }
        return { items: [], totalCount: 0 };
      }
    },
    [loader, perPage, cacheKey],
  );

  // Load the current page whenever it (or perPage) changes.
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPage(pageIdx).then(({ items: pageItems, totalCount: tc }) => {
      if (cancelled) return;
      setItems(pageItems);
      setTotalCount(tc);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [pageIdx, perPage, fetchPage]);

  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));

  // Idle-time prefetch of the next page to make "next" instantaneous.
  // Skips when the tab is hidden (document.hidden) to avoid burning data on
  // backgrounded tabs — the page will prefetch when the user returns.
  React.useEffect(() => {
    if (loading) return;
    if (pageIdx + 1 >= totalPages) return;
    if (typeof document !== "undefined" && document.hidden) return;

    const run = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      fetchPage(pageIdx + 1);
    };
    if (
      typeof window !== "undefined" &&
      typeof window.requestIdleCallback === "function"
    ) {
      const id = window.requestIdleCallback(run, { timeout: 1200 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(run, 400);
    return () => clearTimeout(t);
  }, [pageIdx, totalPages, loading, fetchPage]);

  const clampedSetPage = React.useCallback(
    (i) => {
      if (typeof i !== "number" || Number.isNaN(i)) return;
      const next = Math.min(Math.max(0, i), Math.max(0, totalPages - 1));
      setPageIdx(next);
    },
    [totalPages],
  );

  return {
    items,
    pageIdx,
    setPageIdx: clampedSetPage,
    totalPages,
    totalCount,
    loading,
  };
}
